import React, { JSX, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import classNames from 'classnames'
import {
  CalendarDaysIcon,
  ChartBarIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  SignalIcon,
  DocumentIcon,
  ArchiveBoxIcon,
  PlusIcon,
  RectangleGroupIcon,
  TrophyIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { useEntitlements, planDisplayName } from 'context/plan'
import { useSocketToken } from 'context/socketToken'
import { useEventsList } from 'context/eventsList'
import { useV1Visible } from 'hooks/useV1Visible'
import { StreamsComingSoonPage } from 'pages/StreamsComingSoonPage'
import { LoadingPage, LoadingSpinner } from 'components/Loading'
import { graphqlRequest } from 'lib/helpers'
import { EventSummary, EventStatus } from 'types/api'
import { BILLING_PATH, EVENTS_PATH, NEW_EVENT_PATH, eventPath } from 'lib/paths'
import createEventMutation from 'graphql/createEvent.graphql'

const DATE_OPTS: Intl.DateTimeFormatOptions = { month: 'short', day: '2-digit', year: 'numeric' }

const STATUS_DOT: Record<EventStatus, string> = {
  ACTIVE: 'bg-emerald-500',
  INACTIVE: 'bg-amber-500',
  ENDED: 'bg-zinc-500'
}

const EventModuleRow = ({ event }: { event: EventSummary }): JSX.Element => (
  <li>
    <Link
      to={eventPath(event.eventId)}
      data-testid={`module-event-${event.eventId}`}
      className='flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2.5 transition-colors hover:border-brand/30 hover:bg-dm-bg-accent/50'
    >
      <div className='flex min-w-0 items-center gap-2.5'>
        <span className={classNames('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[event.status])} />
        <span className='truncate text-sm font-medium text-dm-text-primary'>{event.name}</span>
      </div>
      <span className='shrink-0 text-[11px] text-dm-text-accent'>
        {new Date(event.startedAt ?? event.endedAt ?? event.createdAt).toLocaleDateString(undefined, DATE_OPTS)}
      </span>
    </Link>
  </li>
)

const EventModule = ({
  title,
  icon: Icon,
  events,
  emptyText
}: {
  title: string
  icon: React.ForwardRefExoticComponent<Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
    title?: string
    titleId?: string
  } & React.RefAttributes<SVGSVGElement>>
  events: EventSummary[]
  emptyText: string
}): JSX.Element => (
  <section
    data-testid={`event-module-${title.toLowerCase().replace(/\s+/g, '-')}`}
    className='flex flex-col rounded-xl border border-white/5 bg-dm-bg-secondary p-5'
  >
    <div className='mb-3 flex items-center justify-between'>
      <div className='flex items-center gap-2'>
        <Icon className='size-4 text-dm-text-accent' aria-hidden='true' />
        <h2 className='text-sm font-semibold uppercase tracking-wider text-dm-text-accent'>
          {title}
        </h2>
      </div>
      <span className='inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand/15 px-1.5 text-[10px] font-semibold text-brand'>
        {events.length}
      </span>
    </div>
    {events.length === 0
      ? (
        <div className='flex flex-1 items-center justify-center rounded-lg border border-dashed border-white/10 px-3 py-6 text-center text-xs text-dm-text-secondary'>
          {emptyText}
        </div>
        )
      : (
        <ul className='space-y-2'>
          {events.map((ev) => <EventModuleRow key={ev.eventId} event={ev} />)}
        </ul>
        )}
  </section>
)

const STREAMS_PITCH: Array<{ icon: typeof SignalIcon, text: string }> = [
  { icon: ChartBarIcon, text: 'Live polls and tug-of-wars your viewers vote on straight from chat' },
  { icon: TrophyIcon, text: 'Scoreboards with live score controls during the stream' },
  { icon: RectangleGroupIcon, text: 'Composite desktop + mobile overlays: arrange everything in scenes and take them live' },
  { icon: UserGroupIcon, text: 'Team access: invite editors and co-hosts to run it with you' }
]

const StreamsUpsell = (): JSX.Element => {
  const { limitFor, upgradeForLimit, shopifyBilled } = useEntitlements()
  const ladderUpgrade = upgradeForLimit('eventsPerAccount', limitFor('eventsPerAccount'))
  const upgrade = shopifyBilled ? null : ladderUpgrade
  return (
    <main
      className='flex flex-1 flex-col items-center overflow-y-auto px-8 py-16'
      data-testid='streams-upsell'
    >
      <div className='flex w-full max-w-lg flex-col items-center gap-5 text-center'>
        <div className='flex h-16 w-16 items-center justify-center rounded-full bg-brand/10 text-brand ring-1 ring-brand/20'>
          <SignalIcon className='size-8' aria-hidden='true' />
        </div>
        <h1 className='text-xl font-semibold text-dm-text-primary'>
          Streams are a paid feature
        </h1>
        <p className='text-sm text-dm-text-secondary'>
          A stream ties your interactive content together: audience-driven
          polls, tug-of-wars and scoreboards, arranged in scenes on one
          composite overlay.
        </p>
        <ul className='w-full space-y-2 text-left'>
          {STREAMS_PITCH.map(({ icon: Icon, text }) => (
            <li
              key={text}
              className='flex items-start gap-3 rounded-lg border border-white/5 bg-dm-bg-secondary px-4 py-3'
            >
              <Icon className='mt-0.5 size-5 shrink-0 text-brand' aria-hidden='true' />
              <span className='text-sm text-dm-text-secondary'>{text}</span>
            </li>
          ))}
        </ul>
        <Link
          to={BILLING_PATH}
          data-testid='streams-upsell-upgrade'
          className='inline-flex items-center gap-2 rounded-md bg-brand px-5 py-2.5 text-sm font-semibold text-black hover:bg-brand/80'
        >
          <CheckIcon className='size-4' aria-hidden='true' />
          {upgrade !== null ? `Upgrade to ${planDisplayName(upgrade)}` : 'See plans'}
        </Link>
        <p className='text-[11px] text-dm-text-accent'>
          Your chat overlays and emote walls stay free. Find them under
          Quick Overlays.
        </p>
      </div>
    </main>
  )
}

const EmptyStateLanding = ({ events }: { events: EventSummary[] }): JSX.Element => {
  const { enforcedLimitFor, upgradeForLimit, usage } = useEntitlements()
  const eventCap = enforcedLimitFor('eventsPerAccount')
  const atEventCap = eventCap !== null && usage.events >= eventCap
  const eventUpgrade = atEventCap
    ? upgradeForLimit('eventsPerAccount', eventCap)
    : null

  const active = events.filter((e) => e.status === 'ACTIVE')
  const inactive = events.filter((e) => e.status === 'INACTIVE')
  const ended = events
    .filter((e) => e.status === 'ENDED')
    .sort((a, b) => (b.endedAt ?? b.createdAt).localeCompare(a.endedAt ?? a.createdAt))
    .slice(0, 5)

  return (
    <main
      className='flex flex-1 flex-col overflow-y-auto px-8 py-12'
      data-testid='events-empty-state'
    >
      <header className='mb-10 flex flex-col items-center gap-4 text-center'>
        <div className='flex h-16 w-16 items-center justify-center rounded-full bg-brand/10 text-brand ring-1 ring-brand/20'>
          <CalendarDaysIcon className='size-8' aria-hidden='true' />
        </div>
        <h1 className='text-xl font-semibold text-dm-text-primary'>
          Select a stream to manage
        </h1>
        {atEventCap
          ? (
            <div className='flex flex-col items-center gap-2'>
              <button
                type='button'
                disabled
                data-testid='events-empty-state-create'
                title="You've reached your plan's stream limit"
                className='inline-flex cursor-not-allowed items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black opacity-60'
              >
                <PlusIcon className='size-4' aria-hidden='true' />
                Create stream
              </button>
              <p className='text-[11px] text-dm-text-secondary'>
                You've reached your plan's stream limit ({eventCap}).
                {eventUpgrade !== null && (
                  <> <span className='font-medium text-brand'>{planDisplayName(eventUpgrade)} allows {eventUpgrade.limits.eventsPerAccount ?? 'unlimited'}.</span></>
                )}
              </p>
            </div>
            )
          : (
            <Link
              to={NEW_EVENT_PATH}
              data-testid='events-empty-state-create'
              className='inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80'
            >
              <PlusIcon className='size-4' aria-hidden='true' />
              Create stream
            </Link>
            )}
      </header>

      <div className='mx-auto grid w-full max-w-5xl gap-5 lg:grid-cols-3'>
        <EventModule
          title='Not live'
          icon={DocumentIcon}
          events={inactive}
          emptyText='No streams waiting to go live. Hit + to start one.'
        />
        <EventModule
          title='Live'
          icon={SignalIcon}
          events={active}
          emptyText='No streams listening to chat rooms or third-party services right now.'
        />
        <EventModule
          title='Recently ended'
          icon={ArchiveBoxIcon}
          events={ended}
          emptyText='No streams ended yet.'
        />
      </div>
    </main>
  )
}

const CreateEventForm = ({
  onCreated
}: {
  onCreated: (event: EventSummary) => void
}): JSX.Element => {
  const navigate = useNavigate()
  const { auth0Token } = useAuth()
  const { refresh: refreshSocketToken } = useSocketToken()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCancel = (): void => {
    void navigate(EVENTS_PATH)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    if (auth0Token === undefined || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const { event } = await graphqlRequest<{ event: EventSummary }>(
        createEventMutation,
        { name, description: description.trim().length > 0 ? description : null },
        auth0Token
      )
      onCreated(event)
      void refreshSocketToken()
      void navigate(eventPath(event.eventId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create stream')
      setSubmitting(false)
    }
  }

  return (
    <main className='flex flex-1 items-start justify-center px-8 py-12' data-testid='create-event-form'>
      <form
        onSubmit={(e) => { void handleSubmit(e) }}
        className='w-full max-w-xl rounded-xl border border-white/5 bg-dm-bg-secondary p-6'
      >
        <h1 className='mb-1 text-xl font-semibold text-dm-text-primary'>Create a new stream</h1>
        <p className='mb-6 text-sm text-dm-text-secondary'>
          Streams group polls, tug of wars, and scoreboards. You can edit details later.
        </p>

        {error !== null && (
          <div className='mb-4 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300'>
            <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
            <span>{error}</span>
          </div>
        )}

        <label className='mb-4 flex flex-col gap-1.5'>
          <span className='text-xs font-medium uppercase tracking-wider text-dm-text-accent'>Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={submitting}
            placeholder='Summer Gaming Festival 2024'
            className='rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
          />
        </label>

        <label className='mb-6 flex flex-col gap-1.5'>
          <span className='text-xs font-medium uppercase tracking-wider text-dm-text-accent'>Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
            rows={3}
            placeholder='Optional context'
            className='rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
          />
        </label>

        <div className='flex items-center justify-end gap-3'>
          <button
            type='button'
            onClick={handleCancel}
            disabled={submitting}
            className='rounded-md px-4 py-2 text-sm font-medium text-dm-text-secondary hover:text-dm-text-primary disabled:opacity-60'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={submitting || name.trim().length === 0}
            className='inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {submitting ? <><LoadingSpinner /> Creating…</> : 'Create stream'}
          </button>
        </div>
      </form>
    </main>
  )
}

export const EventsPage = (): JSX.Element | null => {
  const { auth0Token, streamToolsUser } = useAuth()
  const { pathname } = useLocation()
  const { events, loading, error, refetch, prependEvent } = useEventsList()
  const { status: planStatus, limitFor } = useEntitlements()
  const streamsLocked = planStatus === 'ready' && limitFor('eventsPerAccount') === 0
  const v1Visible = useV1Visible()

  if (streamToolsUser === null) return <div>Unauthorized</div>
  if (auth0Token === undefined) return <LoadingPage />

  if (!v1Visible) return <StreamsComingSoonPage />

  if (streamsLocked) return <StreamsUpsell />

  const isCreating = pathname === NEW_EVENT_PATH

  const handleCreated = (created: EventSummary): void => {
    prependEvent(created)
  }

  if (isCreating) {
    return <CreateEventForm onCreated={handleCreated} />
  }
  if (error !== null) {
    return (
      <main className='flex flex-1 items-center justify-center px-8 py-16'>
        <div className='flex max-w-md flex-col items-center gap-3 text-center text-sm'>
          <ExclamationTriangleIcon className='size-8 text-red-400' aria-hidden='true' />
          <p className='text-red-300'>{error}</p>
          <button
            onClick={() => { void refetch() }}
            className='rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
          >
            Retry
          </button>
        </div>
      </main>
    )
  }
  if (loading && events.length === 0) {
    return (
      <main className='flex flex-1 items-center justify-center px-8 py-16'>
        <LoadingSpinner />
      </main>
    )
  }
  return <EmptyStateLanding events={events} />
}

export type { EventStatus }
